import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { Button } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import {
  useGetLabReportQuestionnaire,
  usePostLabReportQuestionnaire,
  useUploadLabReportFile,
} from 'services/interpret-lab-report/hooks';
import Loader from 'components/Loader';
import {
  QuestionType,
  ReportQuestion as ReportQuestionType,
} from '../../../types';
import ReportQuestion from './ReportQuestion';
import { getCurrentTimestamp, getTimezone } from 'lib/utils';
import FinalReport from './FinalReport';
import useAuthStore from 'store/auth';

export default function GenerateLabReportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const locale = useAuthStore((store) => store.locale);

  const [currentQA, setCurrentQA] = useState<ReportQuestionType>();
  const [selectedEngChoices, setSelectedEngChoices] = useState<string[]>([]);
  const [selectedEsChoices, setSelectedEsChoices] = useState<string[]>([]);
  const [userInput, setUserInput] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File>();
  const [isFinalMsg, setIsFinalMsg] = useState<boolean>(false);

  const { data: reportQuestion, isFetching } =
    useGetLabReportQuestionnaire('latest');

  useEffect(() => {
    if (reportQuestion) setCurrentQA(reportQuestion?.[0]);
  }, [reportQuestion]);

  const { mutate, isPending } = usePostLabReportQuestionnaire({
    onSuccess: (data: ReportQuestionType) => {
      setUserInput('');
      setSelectedEngChoices([]);
      setSelectedEsChoices([]);

      if (data) {
        setCurrentQA(data);
      }
      setIsFinalMsg(
        ['final_msg', 'final_message'].includes(data?.path_type) ||
          data.question_type === QuestionType.FINAL_MESSAGE
      );
    },
    onError: () => toast.error(t('failed_response_submit_msg')),
  });

  const { mutate: uploadReportFile, isPending: isUploading } =
    useUploadLabReportFile({
      onSuccess: async ({ token_id }) => {
        if (token_id) {
          onPostQuestionnaire(token_id, token_id);
        }
      },
    });

  const isNotScanPath = useMemo(
    () =>
      currentQA
        ? ['final_msg', 'config', 'no_scan_data'].includes(
            currentQA?.path_type
          ) || currentQA?.question_type === QuestionType.SUBMITTED
        : false,
    [currentQA]
  );

  const onPostQuestionnaire = (
    eng_choices: string,
    spanish_choices: string
  ) => {
    mutate({
      q_id: currentQA?.q_id || '',
      answer_id: currentQA?.answer_id || '',
      eng_choices,
      spanish_choices,
      timestamp: getCurrentTimestamp(),
      timezone: getTimezone(),
      path_type: currentQA?.path_type || '',
    });
  };

  const onSelectMultiChoices = useCallback(
    (checked: boolean, engChoice: string, spanishChoice: string) => {
      if (checked) {
        const englishChoices = [...selectedEngChoices];
        englishChoices.push(engChoice);
        setSelectedEngChoices(englishChoices);

        const spanishChoices = [...selectedEsChoices];
        spanishChoices.push(spanishChoice);
        setSelectedEsChoices(spanishChoices);
      } else {
        setSelectedEngChoices((prev) => prev.filter((p) => p !== engChoice));
        setSelectedEsChoices((prev) => prev.filter((p) => p !== spanishChoice));
      }
    },
    [selectedEngChoices, selectedEsChoices]
  );

  const onSubmittedSelect = useCallback(
    (engChoice: string, spanishChoice: string) => {
      if (engChoice.includes('Exit')) {
        navigate(PATH_ROUTES.INTERPRET_LAB_REPORT);
        return;
      }

      if (engChoice.includes('Initiate Scan')) {
        navigate(
          PATH_ROUTES.START_FACE_SCAN +
            `?redirectTo=${PATH_ROUTES.GENERATE_LAB_REPORT}`
        );
        return;
      }

      onPostQuestionnaire(
        JSON.stringify([engChoice]),
        JSON.stringify([spanishChoice])
      );
    },
    [currentQA]
  );

  const onFileUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    uploadReportFile(formData);
  };

  const onSubmit = async () => {
    if (currentQA?.question_type === QuestionType.FILE_UPLOAD) {
      onFileUpload();
      return;
    }

    let eng_choices: string = userInput;
    let spanish_choices: string = userInput;

    if (currentQA?.question_type === QuestionType.MULTI_SELECT) {
      eng_choices = JSON.stringify(selectedEngChoices);
      spanish_choices = JSON.stringify(selectedEsChoices);
    }

    onPostQuestionnaire(eng_choices, spanish_choices);
  };

  const saveDisabled = useMemo(
    () =>
      currentQA?.question_type === QuestionType.FILE_UPLOAD && !selectedFile,
    [currentQA, selectedFile]
  );

  if (isFetching || isPending || isUploading) {
    return <Loader className='h-[calc(100vh-8rem)]' />;
  }

  if (isFinalMsg) {
    return (
      <FinalReport
        tokenId={currentQA?.token_id || ''}
        retry={currentQA?.retry || 0}
        retryDelay={currentQA?.retryDelay || 5000}
        message={
          locale === 'es'
            ? currentQA?.spanish_question
            : currentQA?.eng_question
        }
      />
    );
  }

  return (
    <div className='mx-4 mt-4 mb-8 lg:my-4'>
      <div className='mb-3 lg:hidden'>
        <Button
          variant='outline'
          onClick={() => navigate(-1)}
          className='!h-8 text-blue-500 pr-5 border-blue-500'
        >
          <span>
            <ChevronLeft className='size-4 mb-0.5 mr-0.5' />
          </span>
          {t('back')}
        </Button>
      </div>

      <div className='flex items-center justify-center lg:h-[calc(100vh-8rem)] overflow-hidden'>
        <div className='max-w-md mx-auto space-y-4 text-center lg:space-y-8'>
          <h1 className='text-2xl font-semibold tracking-tight scroll-m-20'>
            {t('lab_report_upload')}
          </h1>
          {isNotScanPath ? null : (
            <div className='mx-12'>
              <p className='leading-tight'>
                {t('lab_report_questionnaire_text')}
              </p>
            </div>
          )}
          <div className='space-y-2'>
            <ReportQuestion
              question={currentQA}
              userInput={userInput}
              selectedChoices={selectedEngChoices}
              setUserInput={setUserInput}
              onSelect={onSelectMultiChoices}
              onSubmittedSelect={onSubmittedSelect}
              setSelectedFile={setSelectedFile}
              selectedFile={selectedFile}
            />
          </div>
          <div className='flex justify-center w-full gap-x-4'>
            {isNotScanPath ? (
              <>
                {isFinalMsg && (
                  <Button
                    variant='outline'
                    className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
                    onClick={() => navigate(PATH_ROUTES.INTERPRET_LAB_REPORT)}
                  >
                    {t('go_back')}
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant='outline'
                className='px-6 text-gray-700 border-gray-700 rounded-full'
                disabled={saveDisabled}
                onClick={onSubmit}
              >
                {t('save_and_next')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
